import dayjs from 'dayjs';
import type { CohortSpendMember, CohortSpendReport } from '@/types';

const inrFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** Rupees with Indian digit grouping and no decimals; '—' for null. */
export function inr(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '—' : `₹${inrFormat.format(n)}`;
}

/** A count with Indian digit grouping. */
export function count(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '—' : inrFormat.format(n);
}

/** Share as a whole percent ('0%' when the total is 0). */
export function pct(part: number, total: number): string {
  return total > 0 ? `${Math.round((part / total) * 100)}%` : '0%';
}

/** A UTC ISO time shown in the browser's zone (IST for the team): "2 Nov 2026". */
export function day(iso: string | null): string {
  return iso ? dayjs(iso).format('D MMM YYYY') : '—';
}

/** The cohort's spend rule in words, e.g. "₹1,000+" or "₹200-₹999" (a 999.99 bound reads as 999). */
export function ruleText(range: CohortSpendReport['spendingRange']): string | null {
  if (!range) return null;
  if (range.min !== null && range.max !== null)
    return `${inr(range.min)}-${inr(Math.floor(range.max))}`;
  if (range.min !== null) return `${inr(range.min)}+`;
  return `up to ${inr(Math.floor(range.max ?? 0))}`;
}

/**
 * The warning shown when members no longer fit the spend rule, else null.
 * The usual cause: added under an older rule, then not seen for 35+ days, so the
 * daily run stopped re-checking them; Recalculate on this cohort removes them.
 */
export function staleWarning(report: CohortSpendReport): string | null {
  const fits = report.summary.spendFitsRule;
  const members = report.summary.members;
  if (fits === null || fits >= members) return null;
  const off = members - fits;
  return (
    `${count(off)} of ${count(members)} members (${pct(off, members)}) no longer fit this cohort's ` +
    `spend rule (${ruleText(report.spendingRange)}). ${count(report.summary.notSeen35d)} members ` +
    `have not been seen for 35+ days, so the daily recalculation no longer re-checks them. ` +
    `Press Recalculate on this cohort to remove the ones that don't fit.`
  );
}

/**
 * One CSV cell. Player names are player-typed, so a cell that starts like a formula (=, +, -, @,
 * tab, CR) gets a leading ' so Excel / Sheets show it as text instead of running it.
 */
export const csvEscape = (raw: string) => {
  const s = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\r\n']/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Members as CSV (UTF-8 with BOM so Excel reads ₹ and names). Dates stay UTC ISO. */
export function membersCsv(members: CohortSpendMember[]): string {
  const header = [
    'userId',
    'displayName',
    'platform',
    'spendInr',
    'purchases',
    'firstPurchaseAtUtc',
    'lastPurchaseAtUtc',
    'lastSeenAtUtc',
    'fitsSpendRule',
    'addedAtUtc',
    'source',
  ];
  const rows = members.map((m) => [
    m.userId,
    m.displayName ?? '',
    m.platform ?? '',
    String(m.inr),
    String(m.purchases),
    m.firstPurchaseAt ?? '',
    m.lastPurchaseAt ?? '',
    m.lastSeenAt ?? '',
    m.spendFitsRule === null ? '' : m.spendFitsRule ? 'yes' : 'no',
    m.assignedAt,
    m.source,
  ]);
  return `\uFEFF${[header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\r\n')}\r\n`;
}

/** Elements inside a cohort row that do their own thing; a click on them must not open the drawer. */
const ROW_CONTROLS =
  'button, a, input, label, .ant-switch, .ant-popover, .ant-dropdown, .ant-select';

/**
 * Whether a click on a cohort table row should open its spend drawer: yes, unless it landed on
 * one of the row's controls (or outside any element).
 */
export function opensSpendFromRowClick(
  target: Pick<Element, 'closest'> | null | undefined
): boolean {
  if (!target || typeof target.closest !== 'function') return false;
  return target.closest(ROW_CONTROLS) === null;
}
